import Button from '@/components/base/button/button';
import PhosphorIcon from '@/components/base/icon/phosphor-icon';
import {
  NavigationStore,
  useNavigationStore,
} from '@/features/navigation/store/navigation-store';
import type { Navigation } from '@/features/navigation/types/navigation';
import { MENU_DROPDOWN_MAX_ROWS_IN_ONE_COLUMN } from '@/features/navigation/utils/constants';
import { findAndFocusElement } from '@/features/navigation/utils/find-and-focus-element';
import { chunk } from '@/utils/chunk';
import { XCircle } from '@phosphor-icons/react';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import { Children } from 'react';
import { FreeFocusInside } from 'react-focus-lock';
import { LinkItem } from './link-item';

interface Props {
  submenu: Navigation;
  depthLevel: number;
  onMouseEnter?: () => void;
}

const selector = ({
  categoryId,
  subcategoryId,
  resetAll,
}: NavigationStore) => ({
  categoryId,
  subcategoryId,
  resetAll,
});

export const LinksWrapper = ({ submenu, depthLevel, onMouseEnter }: Props) => {
  const t = useTranslations('common');

  const { categoryId, subcategoryId, resetAll } = useNavigationStore(selector);
  const isCurrentCategoryActive = categoryId === submenu.id && depthLevel === 1;
  const isCurrentSubcategoryActive =
    categoryId && subcategoryId === submenu.id && depthLevel === 2;

  const chunkedArray = chunk(
    submenu.children,
    MENU_DROPDOWN_MAX_ROWS_IN_ONE_COLUMN,
  );

  const isOpen = isCurrentCategoryActive || isCurrentSubcategoryActive;

  const onClose = () => {
    findAndFocusElement(`[data-header-button-focus-id="${categoryId}"]`);
    resetAll();
  };

  return (
    <ul
      onMouseEnter={onMouseEnter}
      aria-level={depthLevel + 1}
      hidden={!isOpen}
      id={`second-level-${submenu.id}`}
      data-testid={'links-wrapper'}
      data-isopen={isCurrentSubcategoryActive}
      key={submenu.id}
      className={clsx(
        '-left-m -z-10 absolute top-full w-max flex-col rounded-l border-3 border-decorative bg-main px-l py-m',
        isOpen ? 'flex ' : 'hidden',
        'transition-transform duration-100 ease-in',
        depthLevel === 1
          ? '!w-auto absolute box-content min-w-full'
          : 'relative',
        depthLevel === 2 &&
          '!top-0 !rounded-none !bg-alt left-0 h-auto border-none',
      )}
      style={{
        opacity: isOpen ? 1 : 0,
      }}
    >
      <FreeFocusInside className="h-full w-full">
        {submenu?.href && (
          <LinkItem
            href={submenu.href}
            className="mb-m whitespace-nowrap pr-l"
            entireCategory
            data-subcategory-first-item={isOpen}
          />
        )}

        <div className="flex gap-x-l">
          {chunkedArray?.length >= 1 &&
            Children.toArray(
              chunkedArray.map((column: Navigation[]) => (
                <div
                  className="flex flex-col gap-xs"
                  key={`${subcategoryId}-${Math.random()}`}
                >
                  {column.map(({ id, href, title }) => (
                    <LinkItem key={id} href={href ?? '#'} title={title} />
                  ))}
                </div>
              )),
            )}
        </div>
        {submenu?.href && (
          <Button
            variant="tertiary-no-frame"
            className="absolute top-xxs right-xxs h-clickable-large w-clickable-large"
            aria-label={t('close')}
            onClick={onClose}
            data-subcategory-last-item={isOpen}
            testId={'close-dropdown-button'}
          >
            <PhosphorIcon Icon={XCircle} variant="regular" className="icon-l" />
          </Button>
        )}
      </FreeFocusInside>
    </ul>
  );
};
